import { Link } from 'react-router-dom';
import { Zap, Map, BookOpen, ArrowRight, TrendingUp, Loader2, Building2, Clock, ChevronRight, Search, PlusCircle } from 'lucide-react';
import { MetricCard, SectionTitle } from '../components/Luxury';
import { CruxLogo } from '../components/Logo/CruxLogo';
import { useHomeStats } from '../hooks/useHomeStats';
import { useRecentActivity } from '../hooks/useRecentActivity';
import { useActiveMarket } from '../store/useMarketStore';

/**
 * Format relative time from ISO date string
 */
function formatRelativeTime(dateStr?: string): string {
  if (!dateStr) return '';
  const now = new Date();
  const date = new Date(dateStr);
  const diffMs = now.getTime() - date.getTime();
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;
  const diffDay = Math.floor(diffHr / 24);
  if (diffDay < 30) return `${diffDay}d ago`;
  return `${Math.floor(diffDay / 30)}mo ago`;
}

/**
 * HomePage component - landing dashboard with quick stats and workflow links.
 *
 * Features:
 * - Hero section with CruxLogo and welcome text
 * - Quick stats (total leads, tier breakdown, off-market count)
 * - Workflow quick-link cards (Lead Pipeline, Submarket Intelligence, Playbook)
 * - Recent activity feed
 * - Pipeline breakdown section
 */
export function HomePage() {
  const market = useActiveMarket();
  const { totalLeads, tierCounts, marketCounts, licenseCounts, isLoading, error } = useHomeStats();
  const { activities, isLoading: activityLoading } = useRecentActivity();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-obsidian">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-gold mx-auto mb-4" />
          <p className="text-stone-500">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-obsidian">
        <div className="text-center">
          <Building2 className="w-12 h-12 text-crimson mx-auto mb-4" />
          <p className="text-ivory text-lg mb-2">Failed to load dashboard</p>
          <p className="text-stone-500 text-sm">{error.message}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full bg-obsidian">
      {/* Hero Section */}
      <div className="flex-shrink-0 bg-charcoal border-b border-slate-dark px-4 py-6 sm:px-8 sm:py-8">
        <div className="max-w-7xl mx-auto text-center">
          <CruxLogo variant="stacked" size="lg" theme="light" />
          <h1 className="text-xl sm:text-2xl text-gold mt-4" style={{ fontFamily: '"Russo One", sans-serif' }}>
            Investment Command Center
          </h1>
          <p className="text-stone-500 text-sm mt-1">
            {market.display_name} Multifamily Acquisitions Platform
          </p>
        </div>
      </div>

      {/* Main Content - scrollable */}
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-7xl mx-auto px-4 py-6 sm:px-6 md:px-8 md:py-8 space-y-10">
          {/* Section A: Quick Stats */}
          <section>
            <SectionTitle icon={<TrendingUp size={20} />}>
              Quick Stats
            </SectionTitle>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <MetricCard
                label="Total Leads"
                value={totalLeads}
                variant="highlight"
              />
              <MetricCard
                label="Tier 1 Properties"
                value={tierCounts['Tier1'] || 0}
              />
              <MetricCard
                label="Tier 2 Properties"
                value={tierCounts['Tier2'] || 0}
              />
              <MetricCard
                label="Off-Market Deals"
                value={marketCounts['off-market'] || 0}
                variant="green"
              />
            </div>
          </section>

          {/* Section B: Quick Links */}
          <section>
            <SectionTitle icon={<Zap size={20} />}>
              Workflow Quick Links
            </SectionTitle>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {/* Lead Pipeline Card */}
              <Link
                to="/leads"
                className="bg-charcoal border border-slate-dark shadow-sm hover:border-gold hover:-translate-y-1 hover:shadow-md transition-all duration-300 p-4 sm:p-6 rounded-lg group"
              >
                <div className="flex items-start justify-between mb-4">
                  <Zap className="w-8 h-8 text-gold" />
                  <ArrowRight className="w-5 h-5 text-gold opacity-0 group-hover:opacity-100 group-hover:translate-x-1 transition-all duration-300" />
                </div>
                <h3 className="text-ivory font-semibold text-lg mb-2">Lead Pipeline</h3>
                <p className="text-stone-500 text-sm">
                  Screen and compare investment properties
                </p>
              </Link>

              {/* Source Deals Card */}
              <Link
                to="/source"
                className="bg-charcoal border border-slate-dark shadow-sm hover:border-gold hover:-translate-y-1 hover:shadow-md transition-all duration-300 p-4 sm:p-6 rounded-lg group"
              >
                <div className="flex items-start justify-between mb-4">
                  <Search className="w-8 h-8 text-emerald" />
                  <ArrowRight className="w-5 h-5 text-emerald opacity-0 group-hover:opacity-100 group-hover:translate-x-1 transition-all duration-300" />
                </div>
                <h3 className="text-ivory font-semibold text-lg mb-2">Source Deals</h3>
                <p className="text-stone-500 text-sm">
                  Batch source properties with permit and violation data
                </p>
              </Link>

              {/* New Deal Card */}
              <Link
                to="/deal/new"
                className="bg-charcoal border border-slate-dark shadow-sm hover:border-gold hover:-translate-y-1 hover:shadow-md transition-all duration-300 p-4 sm:p-6 rounded-lg group"
              >
                <div className="flex items-start justify-between mb-4">
                  <PlusCircle className="w-8 h-8 text-amber-400" />
                  <ArrowRight className="w-5 h-5 text-amber-400 opacity-0 group-hover:opacity-100 group-hover:translate-x-1 transition-all duration-300" />
                </div>
                <h3 className="text-ivory font-semibold text-lg mb-2">New Deal</h3>
                <p className="text-stone-500 text-sm">
                  Manually enter and analyze a deal
                </p>
              </Link>

              {/* Submarket Intelligence Card */}
              <Link
                to="/zandi-map"
                className="bg-charcoal border border-slate-dark shadow-sm hover:border-gold hover:-translate-y-1 hover:shadow-md transition-all duration-300 p-4 sm:p-6 rounded-lg group"
              >
                <div className="flex items-start justify-between mb-4">
                  <Map className="w-8 h-8 text-sky-400" />
                  <ArrowRight className="w-5 h-5 text-sky-400 opacity-0 group-hover:opacity-100 group-hover:translate-x-1 transition-all duration-300" />
                </div>
                <h3 className="text-ivory font-semibold text-lg mb-2">Submarket Intelligence</h3>
                <p className="text-stone-500 text-sm">
                  Explore {market.submarket_count} {market.display_name} submarkets with backtested scoring
                </p>
              </Link>

              {/* Negotiation Playbook Card */}
              <Link
                to="/playbook"
                className="bg-charcoal border border-slate-dark shadow-sm hover:border-gold hover:-translate-y-1 hover:shadow-md transition-all duration-300 p-4 sm:p-6 rounded-lg group"
              >
                <div className="flex items-start justify-between mb-4">
                  <BookOpen className="w-8 h-8 text-gold" />
                  <ArrowRight className="w-5 h-5 text-gold opacity-0 group-hover:opacity-100 group-hover:translate-x-1 transition-all duration-300" />
                </div>
                <h3 className="text-ivory font-semibold text-lg mb-2">Negotiation Playbook</h3>
                <p className="text-stone-500 text-sm">
                  AI-powered seller negotiation strategies
                </p>
              </Link>
            </div>
          </section>

          {/* Section B.5: Recent Activity */}
          <section>
            <SectionTitle icon={<Clock className="w-5 h-5" />}>
              Recent Activity
            </SectionTitle>

            {activityLoading ? (
              <div className="space-y-2">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="bg-charcoal border border-slate-dark rounded-lg p-4 animate-pulse">
                    <div className="h-4 bg-graphite rounded w-2/3" />
                  </div>
                ))}
              </div>
            ) : activities.length === 0 ? (
              <div className="text-center py-8 text-stone-400 text-sm">
                No recent activity. Start screening properties from the Lead Pipeline.
              </div>
            ) : (
              <div className="space-y-2">
                {activities.map(activity => {
                  // Tier badge colors
                  const tierColors = {
                    'Tier1': 'bg-emerald-500/15 text-emerald-400',
                    'Tier2': 'bg-gold/15 text-gold',
                    'Tier3': 'bg-amber-500/15 text-amber-400',
                  };
                  const tierColor = tierColors[activity.tier as keyof typeof tierColors] || 'bg-white/10 text-stone-500';

                  // Zandi score color
                  let scoreColor = 'text-stone-400';
                  if (activity.zandi_composite_score) {
                    if (activity.zandi_composite_score > 60) scoreColor = 'text-emerald-400';
                    else if (activity.zandi_composite_score >= 40) scoreColor = 'text-gold';
                    else scoreColor = 'text-crimson';
                  }

                  return (
                    <Link
                      to={`/property/${activity.id}`}
                      key={activity.id}
                      className="group bg-charcoal border border-slate-dark hover:border-gold/50 transition-all duration-200 px-5 py-3 rounded-lg flex flex-col sm:flex-row sm:items-center sm:justify-between"
                    >
                      {/* Left side */}
                      <div className="flex items-center gap-3">
                        <Building2 className="w-4 h-4 text-stone-400" />
                        <span className="text-ivory text-sm font-medium">{activity.address}</span>
                        {activity.tier && (
                          <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider ${tierColor}`}>
                            {activity.tier === 'Tier1' ? 'T1' : activity.tier === 'Tier2' ? 'T2' : 'T3'}
                          </span>
                        )}
                        {activity.total_units && (
                          <span className="text-stone-400 text-xs font-mono">
                            {activity.total_units}u
                          </span>
                        )}
                      </div>

                      {/* Right side */}
                      <div className="flex items-center gap-2 mt-1 sm:mt-0 sm:gap-4">
                        {activity.zandi_composite_score !== undefined && (
                          <span className={`font-mono text-sm ${scoreColor}`}>
                            {activity.zandi_composite_score.toFixed(1)}
                          </span>
                        )}
                        {activity.market_status && (
                          <span className="bg-gold/15 text-gold text-[10px] font-bold px-1.5 py-0.5 rounded uppercase tracking-wider">
                            OFF-MKT
                          </span>
                        )}
                        {activity.created_at && (
                          <span className="text-stone-400 text-xs">
                            {formatRelativeTime(activity.created_at)}
                          </span>
                        )}
                        <ChevronRight className="w-4 h-4 text-stone-300 group-hover:text-gold transition-colors" />
                      </div>
                    </Link>
                  );
                })}
              </div>
            )}
          </section>

          {/* Section C: Pipeline Breakdown */}
          <section>
            <SectionTitle icon={<Building2 size={20} />}>
              Pipeline Overview
            </SectionTitle>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {/* On Market */}
              <div className="bg-charcoal border border-slate-dark shadow-sm p-4 rounded-lg">
                <div className="text-[10px] uppercase tracking-[0.15em] text-stone-500 font-semibold">
                  On Market
                </div>
                <div className="text-lg font-bold font-mono text-ivory mt-1">
                  {marketCounts['on-market'] || 0}
                </div>
              </div>

              {/* Off Market */}
              <div className="bg-charcoal border border-slate-dark shadow-sm p-4 rounded-lg">
                <div className="text-[10px] uppercase tracking-[0.15em] text-stone-500 font-semibold">
                  Off Market
                </div>
                <div className="text-lg font-bold font-mono text-ivory mt-1">
                  {marketCounts['off-market'] || 0}
                </div>
              </div>

              {/* Licensed */}
              <div className="bg-charcoal border border-slate-dark shadow-sm p-4 rounded-lg">
                <div className="text-[10px] uppercase tracking-[0.15em] text-stone-500 font-semibold">
                  Licensed
                </div>
                <div className="text-lg font-bold font-mono text-ivory mt-1">
                  {licenseCounts['licensed'] || 0}
                </div>
              </div>

              {/* Unlicensed */}
              <div className="bg-charcoal border border-slate-dark shadow-sm p-4 rounded-lg">
                <div className="text-[10px] uppercase tracking-[0.15em] text-stone-500 font-semibold">
                  Unlicensed
                </div>
                <div className="text-lg font-bold font-mono text-ivory mt-1">
                  {licenseCounts['unlicensed'] || 0}
                </div>
              </div>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}

export default HomePage;
